import { useEffect, useState } from 'react';
import { sound } from '../game/sound';

/** Small speaker on/off toggle for the game screen's sound. Positioned as an
 * absolutely-positioned corner control OUTSIDE the normal `.stage-top` /
 * `.game-body` flow (see index.css `.mute-button`) -- it is never a sibling
 * inside the measured HUD/D-pad boxes `useGameLayout.ts` sizes the maze
 * canvas against, so it can never perturb that math, and it is never
 * composited over `.game-canvas` (it lives in the top corner, the canvas is
 * centered below the HUD strip). Visually it sits over the HUD/chrome
 * corner, which is allowed -- only the maze canvas itself is off limits.
 *
 * Subscribes to `sound`'s own change event (not just its own click) so a
 * mute toggle made elsewhere -- the pause-menu Settings modal -- is
 * reflected here live, and vice versa; both are mirrors of the same
 * `sound.muted` source of truth. */
export function MuteButton(): JSX.Element {
  const [muted, setMutedState] = useState(sound.muted);

  useEffect(() => sound.subscribe(setMutedState), []);

  const toggle = (): void => {
    sound.setMuted(!muted);
  };

  return (
    <button
      type="button"
      className="mute-button"
      onClick={toggle}
      aria-label={muted ? 'Unmute sound' : 'Mute sound'}
      aria-pressed={muted}
      data-testid="mute-button"
    >
      <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
    </button>
  );
}
